import { createFileRoute, Outlet, redirect, useLocation } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AppSidebar, SupportWidget, MobileDeviceNotice } from '~/components/layout';
import Header from '~/components/layout/header';
import { SidebarInset, SidebarProvider } from '~/components/ui';
import { ROLES } from '~/constant';
import { API_ENDPOINTS, NORMALIZE_URLS } from '~/constant/api-endpoints';
import { ModalManager, RowActionsDropdownPortal } from '~/features/shared/components';
import ModalNotice from '~/features/shared/components/modal-notice';
import api from '~/lib/api';
import { filterStore } from '~/stores/filter-store';
import { useAuthStore } from '~/stores';
import { useAuth, useCommonData } from '~/hooks';
import { getIdFormFromPathname, getMinProductIdFromPathname } from '~/utils/auth-utils';
import { TermsOfService } from '~/components/layout/terms-of-service';
import { OnboardingGate } from '~/features/onboarding/onboarding-gate';
import { GuideTourProvider } from '~/features/guide-tour/provider';
import { GuideTourHost } from '~/features/guide-tour/host';

export const Route = createFileRoute('/_authed')({
	beforeLoad: async ({ context, location }) => {
		if (!context.auth.isAuthenticated && typeof window !== 'undefined') {
			throw redirect({
				to: '/login',
			});
		}

		const { user, role } = context.auth;

		// Check permission for Staff via API
		if (role === ROLES.STAFF && user && typeof window !== 'undefined') {
			try {
				const { queryClient } = context;
				const formAccess = await queryClient.ensureQueryData({
					queryKey: [API_ENDPOINTS.permission.getAccessForm, user?.id, role],
					queryFn: async () => {
						return await api.post<any[]>(API_ENDPOINTS.permission.getAccessForm, {
							id: user.id,
							id_staff: user.id,
							id_staff_action: user.id,
							secret_key: user.secret_key,
							token: user.token,
							role: role,
						});
					},
					staleTime: 1000 * 60 * 5,
				});

				const idForm = getIdFormFromPathname(location.pathname);
				if (idForm !== 0 && !NORMALIZE_URLS.includes(location.pathname)) {
					const hasPermission =
						Array.isArray(formAccess) && formAccess.some((item: any) => item.id_form === idForm);
					if (!hasPermission) {
						if (typeof window !== 'undefined') {
							toast.warning('Bạn không có quyền truy cập vào trang này!');
						}
						throw redirect({ to: `/${role}/dashboard` as any });
					}
				}
				// Kiểm tra quyền truy cập theo gói sản phẩm (product package)
				const minProductId = getMinProductIdFromPathname(location.pathname);
				if (minProductId > 0 && !NORMALIZE_URLS.includes(location.pathname)) {
					const userProductId = user.id_product || 1;
					if (userProductId < minProductId) {
						if (typeof window !== 'undefined') {
							toast.warning('Tính năng này yêu cầu nâng cấp gói dịch vụ!');
						}
						throw redirect({ to: `/${role}/dashboard` as any });
					}
				}
			} catch (error) {
				if (error instanceof Error && error.name === 'Redirect') throw error;
				console.error('Permission check failed in beforeLoad:', error);
			}
		}
		// check onboard
	},
	component: AuthedLayout,
});

function AuthedLayout() {
	const location = useLocation();
	const previousPathRef = useRef<string | null>(null);
	const [mounted, setMounted] = useState(false);
	const { setIdForm, updateUserAction } = useAuthStore();
	const { user, role } = useAuth();

	// Gọi API /staff/package khi người dùng là staff và lưu vào useCommonData ('userPackage')
	const isStaff = role === ROLES.STAFF && !!user?.id;
	const packageEndpoint = API_ENDPOINTS?.[ROLES.STAFF]?.payment?.package || '/package';
	const { data: userPackage } = useCommonData('userPackage', packageEndpoint, {
		enabled: isStaff,
		fetchOnInit: true,
		data: {
			endpoint: ROLES.STAFF,
		},
	});

	useEffect(() => {
		if (userPackage && typeof userPackage === 'object' && Object.keys(userPackage).length > 0) {
			updateUserAction(userPackage);
		}
	}, [userPackage, updateUserAction]);

	useEffect(() => {
		setMounted(true);
	}, []);

	// Reset filters and update idForm when navigating
	useEffect(() => {
		if (!mounted) return;

		const currentPath = location.pathname;

		// Reset filters if navigating to a different page
		if (previousPathRef.current && previousPathRef.current !== currentPath) {
			filterStore.getState().resetAllFilters();
		}

		// Update idForm in store
		const idForm = getIdFormFromPathname(currentPath);
		setIdForm(idForm);

		previousPathRef.current = currentPath;
	}, [location.pathname, mounted, setIdForm]);

	if (!mounted) {
		return null; // Ensure server and initial client render match exactly
	}

	return (
		<GuideTourProvider>
			<OnboardingGate>
				<SidebarProvider className="min-h-screen xl:flex bg-default-50 p-2.5">
					<AppSidebar className="border-0 data-[slot=sidebar-container]:border-none pl-2.5" />
					<SidebarInset className="bg-white overflow-x-hidden shadow-sm px-2.5 md:px-5 pt-2.5 rounded-md md:rounded-xl md:m-2">
						<MobileDeviceNotice />
						<Header />
						<Outlet />
						<ModalManager />
						<ModalNotice />
						<RowActionsDropdownPortal />
						<TermsOfService />
						<GuideTourHost />
						{isStaff && <SupportWidget />}
					</SidebarInset>
				</SidebarProvider>
			</OnboardingGate>
		</GuideTourProvider>
	);
}
